import { useState, useEffect, useCallback, useRef } from 'react'
import { Calendar as CalendarIcon, Clock } from 'lucide-react'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function DateTimePickerContent({ selectedDate, onDateSelect, onTimeChange }) {
  return (
    <div className="w-full min-w-0">
      <Calendar mode="single" selected={selectedDate} onSelect={onDateSelect} />
      <div className="p-3 border-t border-border/10">
        <div className="flex items-center gap-2">
          <Clock className="ml-0.5 h-4 w-4 text-muted-foreground" />
          <Label htmlFor="time" className="text-xs font-medium">
            Time
          </Label>
          <Input
            id="time"
            type="time"
            className="h-8 text-xs"
            value={selectedDate ? format(selectedDate, 'HH:mm') : ''}
            onChange={onTimeChange}
          />
        </div>
      </div>
    </div>
  )
}

export function DateTimePicker({
  date,
  setDate,
  placeholder = 'Pick a date',
  className,
  trigger,
  showConfirm = false,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const onOpenChange = externalOnOpenChange !== undefined ? externalOnOpenChange : setInternalOpen
  const triggerRef = useRef(null)
  const contentRef = useRef(null)

  const [selectedDate, setSelectedDate] = useState(date)

  // Sync internal state with prop
  useEffect(() => {
    setSelectedDate(date)
  }, [date])

  const handleDateSelect = useCallback(
    (newDate) => {
      if (newDate) {
        const currentDate = selectedDate || new Date()
        newDate.setHours(currentDate.getHours(), currentDate.getMinutes())
        setSelectedDate(newDate)
        if (!showConfirm) {
          setDate(newDate)
        }
      } else {
        setSelectedDate(undefined)
        if (!showConfirm) {
          setDate(undefined)
        }
      }
    },
    [selectedDate, setDate, showConfirm],
  )

  const handleTimeChange = useCallback(
    (e) => {
      const timeStr = e.target.value
      let newDate
      if (!selectedDate) {
        // If no date selected but time changed, assume today
        newDate = new Date()
      } else {
        newDate = new Date(selectedDate.getTime())
      }

      const [hours, minutes] = timeStr.split(':').map(Number)
      newDate.setHours(hours, minutes)
      setSelectedDate(newDate)
      if (!showConfirm) {
        setDate(newDate)
      }
    },
    [selectedDate, setDate, showConfirm],
  )

  const handleConfirm = useCallback(() => {
    setDate(selectedDate)
    onOpenChange(false)
  }, [selectedDate, setDate, onOpenChange])

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleDocumentPointerDown = (event) => {
      const target = event.target

      if (!(target instanceof Node)) {
        return
      }

      if (contentRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return
      }

      onOpenChange(false)
    }

    document.addEventListener('pointerdown', handleDocumentPointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
    }
  }, [open, onOpenChange])

  return (
    <Popover modal={false} open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <span ref={triggerRef} className="contents">
          {trigger || (
            <Button
              variant={'outline'}
              className={cn(
                'w-full min-w-0 justify-start text-left font-normal h-7 text-xs',
                !date && 'text-muted-foreground',
                className,
              )}
            >
              <CalendarIcon className="mr-2 h-3 w-3" />
              <span className="truncate">{date ? format(date, 'PP p') : placeholder}</span>
            </Button>
          )}
        </span>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-[min(22rem,calc(100vw-1rem))] p-0"
        align="start"
      >
        <DateTimePickerContent
          selectedDate={selectedDate}
          onDateSelect={handleDateSelect}
          onTimeChange={handleTimeChange}
        />
        {showConfirm && (
          <div className="p-3 border-t border-border/10 bg-muted/20">
            <Button
              className="w-full h-8 text-xs font-bold uppercase tracking-wider"
              onClick={handleConfirm}
              disabled={!selectedDate}
            >
              Confirm
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
